/**
 * @voltxampere/engine — MOSFET DC stamp (NMOS + PMOS, Level 1 + Level 2)
 *
 * Sprint 7 shipped the SPICE Level 1 (Shichman-Hodges) square-law
 * model. Sprint 24 B2 layers the SPICE Level 2 (Grove-Frohman)
 * effects on top:
 *
 *   - **Theta** (vertical-field mobility degradation):
 *     `K_eff = Kp / (1 + Theta·max(0, V_OV))`. Reduces transconductance
 *     when the gate is heavily overdriven — the field at the channel
 *     surface starts limiting carrier velocity. Standard SPICE THETA
 *     parameter, units 1/V.
 *
 *   - **Eta** (drain-induced barrier lowering, "static feedback"):
 *     `V_TH_eff = V_TH_body − Eta·V_DS`. Lowers the effective threshold
 *     as drain bias grows — the drain pulls the channel down. Distinct
 *     from λ (channel-length modulation), which scales `I_D` magnitude
 *     in saturation rather than shifting `V_TH`.
 *
 *   - **Phi + Gamma** (body effect):
 *     `V_TH_body = V_T0 + γ·(√(max(0, Phi − V_BS)) − √Phi)`.
 *     Threshold rises with reverse body bias (V_BS < 0). Sprint 24
 *     B2's 3-pin device has no body terminal so V_BS is pinned at 0;
 *     the formula is in the code path for the future 4-pin upgrade
 *     but degenerates to V_TH_body = V_T0 today. Adding Gamma to the
 *     schema keeps the body-effect path type-clean.
 *
 * Three regimes (NMOS convention; PMOS via the polarity sign):
 *
 *   V_TH_eff = V_TH_body − Eta·V_DS
 *   V_OV     = V_GS − V_TH_eff
 *   α        = 1 + Theta · max(0, V_OV)
 *
 *   Cutoff   (V_OV ≤ 0):
 *     I_D = 0, gm = 0, gds = 0
 *
 *   Triode   (0 < V_DS < V_OV):
 *     I_D  = (β/α) · (V_OV·V_DS − V_DS²/2) · (1 + λ·V_DS)
 *     gm, gds via chain rule (see derivation below).
 *
 *   Saturation (V_DS ≥ V_OV):
 *     I_D  = (β/(2α)) · V_OV² · (1 + λ·V_DS)
 *
 * **Jacobian derivation** (saturation, NMOS):
 *
 *   ∂V_OV/∂V_GS = 1, ∂V_OV/∂V_DS = Eta
 *   ∂α/∂V_GS    = Theta, ∂α/∂V_DS = Theta·Eta
 *
 *   gm  (sat) = (β/α²) · (1+λV_DS) · V_OV · (1 + Theta·V_OV/2)
 *   gds (sat) = (β·V_OV)/(2α²) · [α·(2·Eta·(1+λV_DS) + V_OV·λ)
 *                                − V_OV·(1+λV_DS)·Theta·Eta]
 *
 *   Level-1 reduction: with Theta=Eta=0, α=1, the formulas collapse
 *   to gm = β·V_OV·(1+λV_DS) and gds = (β/2)·V_OV²·λ — bit-identical
 *   to the Sprint 7 path. Verified by the Sprint 5/7 + Sprint 24 B1
 *   frozen-value regressions and by the Sprint 24 B2 zero-default
 *   sanity test.
 *
 * Triode follows the same chain-rule pattern; closed-form below.
 *
 * Body diode is NOT stamped — Sprint 7+ defers full BSIM3/4 to Faz 2.
 * pnjlim is not applied because Shichman-Hodges + Grove-Frohman has
 * no exponential to overflow. PMOS mirrors NMOS via the `polarity`
 * sign: `Vgs_eff = sign · Vgs`, `Vds_eff = sign · Vds`, and every
 * current contribution is negated at the RHS write.
 *
 * Gate carries no DC current (ideal MOSFET), so the gate row / col
 * admittance contributions are all zero; only drain / source see
 * non-trivial stamps.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
/**
 * Sprint 24 B2 — Level 2 effective threshold computed with body
 * effect. The 3-pin device pins V_BS at 0; the formula is preserved
 * for the future 4-pin upgrade so plugging in a body terminal lights
 * up the body-effect physics without touching the stamp body.
 */
function levelTwoBodyAdjustedVth(Vt0, Phi, gamma, Vbs) {
    if (gamma <= 0 || Phi <= 0)
        return Vt0;
    const sqrtPhi = Math.sqrt(Phi);
    const arg = Phi - Vbs;
    const sqrtArg = arg > 0 ? Math.sqrt(arg) : 0;
    return Vt0 + gamma * (sqrtArg - sqrtPhi);
}
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Read all four Level 2 parameters and decide whether the stamp
 * needs to engage the Level 2 path. Engagement criterion: any of
 * Theta, Eta, Phi, Gamma is strictly positive. Otherwise the
 * Sprint 7 / Sprint 8a Level 1 fast path runs verbatim — that
 * keeps Sprint 5/7/8a frozen sentinels bit-identical.
 */
function readLevelTwoExtras(component) {
    const Theta = getNumericParam(component, "Theta", 0);
    const Eta = getNumericParam(component, "Eta", 0);
    const Phi = getNumericParam(component, "Phi", 0);
    const Gamma = getNumericParam(component, "Gamma", 0);
    const engaged = Theta > 0 || Eta > 0 || Phi > 0 || Gamma > 0;
    return { engaged, Theta, Eta, Phi, Gamma };
}
function mosfetStampCommon(ctx, component, polarity) {
    const Kp = getNumericParam(component, "Kp", 2e-5);
    const Vt0 = getNumericParam(component, "Vt0", polarity === "nmos" ? 0.7 : -0.7);
    const Lambda = getNumericParam(component, "Lambda", 0.01);
    const W = getNumericParam(component, "W", 10e-6);
    const L = getNumericParam(component, "L", 1e-6);
    const mosfetCtx = { stampType: "mosfet", componentId: component.id };
    if (!(Kp > 0) || !(W > 0) || !(L > 0)) {
        throw new StampParameterError(`mosfetDCStamp: component "${component.id}" has non-positive Kp/W/L (got Kp=${String(Kp)}, W=${String(W)}, L=${String(L)})`, { meta: { ...mosfetCtx, parameter: "Kp/W/L", received: { Kp, W, L }, expected: "all > 0" } });
    }
    if (!Number.isFinite(Vt0) || !Number.isFinite(Lambda) || Lambda < 0) {
        throw new StampParameterError(`mosfetDCStamp: component "${component.id}" has invalid Vt0 / Lambda (got Vt0=${String(Vt0)}, Lambda=${String(Lambda)})`, {
            meta: {
                ...mosfetCtx,
                parameter: "Vt0/Lambda",
                received: { Vt0, Lambda },
                expected: "finite, Lambda >= 0",
            },
        });
    }
    const beta = Kp * (W / L);
    const G = ctx.nodeIndex(getTerminalNetId(component, "gate"));
    const D = ctx.nodeIndex(getTerminalNetId(component, "drain"));
    const S = ctx.nodeIndex(getTerminalNetId(component, "source"));
    // Sprint 24 B2 — Level 2 parameter readout. With every Level 2
    // field unset (Sprint 22 zero-default), `extras.engaged` is `false`
    // and the stamp body bypasses every Grove-Frohman calculation —
    // the Level 1 path runs unchanged.
    const extras = readLevelTwoExtras(component);
    const sign = polarity === "nmos" ? 1 : -1;
    const vG = nodeVoltage(ctx.xPrev, G);
    const vD = nodeVoltage(ctx.xPrev, D);
    const vS = nodeVoltage(ctx.xPrev, S);
    // Polarity-flipped operating voltages; NMOS convention throughout
    // the body.
    const Vgs = sign * (vG - vS);
    const Vds = sign * (vD - vS);
    // Sprint 24 B2 — V_BS pinned at 0 because the 3-pin device has
    // no body terminal. Future 4-pin upgrade plugs in `nodeIndex("body")`
    // and reads V_B from xPrev here.
    const Vbs = 0;
    // Body-effect-adjusted threshold. Without a body terminal Vbs=0,
    // so this evaluates to Vt0 verbatim — body effect is in the code
    // path for the future 4-pin upgrade.
    const VthBody = extras.engaged
        ? levelTwoBodyAdjustedVth(Vt0, extras.Phi, extras.Gamma, Vbs)
        : Vt0;
    // DIBL (static feedback): V_TH shrinks with V_DS via Eta.
    const Vth = sign * VthBody;
    const VthEff = extras.engaged ? Vth - extras.Eta * Vds : Vth;
    const Vov = Vgs - VthEff;
    // Mobility degradation factor α. Defaults to 1 when Theta=0 or
    // V_OV ≤ 0 (cutoff — no current to scale anyway).
    const alpha = extras.engaged && Vov > 0 ? 1 + extras.Theta * Vov : 1;
    const alphaSq = alpha * alpha;
    let Id = 0;
    let gm = 0;
    let gds = 0;
    if (Vov > 0) {
        const onePlusLVds = 1 + Lambda * Vds;
        if (Vds < Vov) {
            // ─── Triode ─────────────────────────────────────────────
            // I_D = (β/α) · u · (1+λV_DS), u = V_OV·V_DS − V_DS²/2
            // Define h = u·(1+λV_DS); I_D = β·h/α.
            const u = Vov * Vds - 0.5 * Vds * Vds;
            const h = u * onePlusLVds;
            Id = (beta * h) / alpha;
            // Chain-rule derivatives. With ∂V_OV/∂V_GS = 1, ∂V_OV/∂V_DS = Eta,
            // ∂α/∂V_GS = Theta, ∂α/∂V_DS = Theta·Eta.
            //
            //   ∂u/∂V_GS = V_DS, ∂u/∂V_DS = V_OV + (Eta − 1)·V_DS
            //   ∂h/∂V_GS = V_DS · (1+λV_DS)
            //   ∂h/∂V_DS = (V_OV + (Eta − 1)·V_DS)·(1+λV_DS) + u·λ
            //
            //   gm  = (∂h/∂V_GS · α − h · ∂α/∂V_GS) · β / α²
            //   gds = (∂h/∂V_DS · α − h · ∂α/∂V_DS) · β / α²
            const dhdVgs = Vds * onePlusLVds;
            const dudVds = Vov + (extras.Eta - 1) * Vds;
            const dhdVds = dudVds * onePlusLVds + u * Lambda;
            const dAlphaVgs = extras.engaged ? extras.Theta : 0;
            const dAlphaVds = extras.engaged ? extras.Theta * extras.Eta : 0;
            gm = (beta * (dhdVgs * alpha - h * dAlphaVgs)) / alphaSq;
            gds = (beta * (dhdVds * alpha - h * dAlphaVds)) / alphaSq;
        }
        else {
            // ─── Saturation ─────────────────────────────────────────
            // I_D = (β/(2α)) · V_OV² · (1+λV_DS)
            // Let f = V_OV²·(1+λV_DS); I_D = (β/2) · f / α.
            const VovSq = Vov * Vov;
            const f = VovSq * onePlusLVds;
            Id = (0.5 * beta * f) / alpha;
            // gm  = (β/2) / α² · (∂f/∂V_GS · α − f · ∂α/∂V_GS)
            // gds = (β/2) / α² · (∂f/∂V_DS · α − f · ∂α/∂V_DS)
            //
            //   ∂f/∂V_GS = 2·V_OV·1·(1+λV_DS) = 2·V_OV·(1+λV_DS)
            //   ∂f/∂V_DS = 2·V_OV·Eta·(1+λV_DS) + V_OV²·λ
            const dfdVgs = 2 * Vov * onePlusLVds;
            const dfdVds = 2 * Vov * extras.Eta * onePlusLVds + VovSq * Lambda;
            const dAlphaVgs = extras.engaged ? extras.Theta : 0;
            const dAlphaVds = extras.engaged ? extras.Theta * extras.Eta : 0;
            gm = (0.5 * beta * (dfdVgs * alpha - f * dAlphaVgs)) / alphaSq;
            gds = (0.5 * beta * (dfdVds * alpha - f * dAlphaVds)) / alphaSq;
        }
    }
    // Admittance stamp — gate is current-free (MOSFET ideal at DC),
    // drain carries Id, source carries -Id.
    //   dId/dVG =  gm      (via Vgs)
    //   dId/dVD =  gds     (via Vds)
    //   dId/dVS = -(gm + gds)
    // Polarity flip cancels between current sign and voltage-direction
    // sign for the admittance block (same trick as BJT), so only the
    // RHS writes flip.
    ctx.addY(D, G, gm);
    ctx.addY(D, D, gds);
    ctx.addY(D, S, -(gm + gds));
    ctx.addY(S, G, -gm);
    ctx.addY(S, D, -gds);
    ctx.addY(S, S, gm + gds);
    // Norton-equivalent current.  The admittance row partials are
    // polarity-invariant (the chain-rule double sign-flip cancels —
    // see the file-level comment), so the linearisation subtraction
    // `admittance·V_op` does NOT carry a polarity sign.  Only the
    // current term `Id` (which is in NMOS convention) needs the
    // outer polarity flip:
    //
    //   "current OUT of drain net" = sign·I_D_NMOS_conv
    //   stamp linearisation → row D: J·V = rhs[D] + (J·V_op − sign·I_D_op)
    //
    // Sprint 24 B2 fix: the original Sprint 7 stamp wrapped the entire
    // residual with `sign`, which ate the polarity inversion needed
    // for PMOS and produced ~7× too much drain current at the OP.
    // The Sprint 5/6/7 NMOS frozen sentinels are unaffected (NMOS
    // sign=+1 makes the buggy and fixed formulas algebraically
    // identical); the PMOS path now passes the polarity-mirror test.
    const admittanceV = gm * vG + gds * vD - (gm + gds) * vS;
    const IeqD = sign * Id - admittanceV;
    const IeqS = -IeqD;
    if (D >= 0)
        ctx.rhs[D] = (ctx.rhs[D] ?? 0) - IeqD;
    if (S >= 0)
        ctx.rhs[S] = (ctx.rhs[S] ?? 0) - IeqS;
}
export const mosfetNmosDCStamp = (ctx, component) => {
    mosfetStampCommon(ctx, component, "nmos");
};
export const mosfetPmosDCStamp = (ctx, component) => {
    mosfetStampCommon(ctx, component, "pmos");
};
export const mosfetNmosDCStampDefinition = defineDCStamp("mosfet-nmos", mosfetNmosDCStamp, []);
export const mosfetPmosDCStampDefinition = defineDCStamp("mosfet-pmos", mosfetPmosDCStamp, []);
//# sourceMappingURL=mosfet.js.map